import { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { QuizQuestion } from '../types';

interface PackageQuizProps {
  questions: QuizQuestion[];
  onComplete: (recommendation: string) => void;
}

const PackageQuiz = ({ questions, onComplete }: PackageQuizProps) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);
  const [selectedOption, setSelectedOption] = useState<string>('');

  const handleNext = () => {
    if (selectedOption) {
      const newAnswers = [...answers, selectedOption];
      setAnswers(newAnswers);

      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
        setSelectedOption('');
      } else {
        calculateRecommendation(newAnswers);
      }
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setSelectedOption(answers[currentQuestion - 1]);
      setAnswers(answers.slice(0, -1));
    }
  };

  const calculateRecommendation = (finalAnswers: string[]) => {
    const scores = { starter: 0, professional: 0, enterprise: 0 };

    finalAnswers.forEach((answer, index) => {
      const question = questions[index];
      const option = question.options.find((opt) => opt.value === answer);
      if (option) {
        scores.starter += option.tierWeight.starter;
        scores.professional += option.tierWeight.professional;
        scores.enterprise += option.tierWeight.enterprise;
      }
    });

    const recommendation =
      scores.enterprise > scores.professional && scores.enterprise > scores.starter
        ? 'Enterprise'
        : scores.professional > scores.starter
        ? 'Professional' :'Starter';

    onComplete(recommendation);
  };

  const progress = ((currentQuestion + 1) / questions.length) * 100;
  const question = questions[currentQuestion];

  return (
    <div className="bg-card rounded-xl border border-border p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-text-primary">Find Your Perfect Plan</h3>
          <span className="text-sm text-text-secondary">
            Question {currentQuestion + 1} of {questions.length}
          </span>
        </div>
        <div className="w-full bg-surface rounded-full h-2 overflow-hidden">
          <div
            className="bg-primary h-full transition-all duration-fast"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="mb-8">
        <h4 className="text-lg font-semibold text-text-primary mb-4">{question.question}</h4>
        <div className="space-y-3">
          {question.options.map((option) => (
            <button
              key={option.value}
              onClick={() => setSelectedOption(option.value)}
              className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-fast ${
                selectedOption === option.value
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-surface'
              }`}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    selectedOption === option.value
                      ? 'border-primary bg-primary' :'border-border'
                  }`}
                >
                  {selectedOption === option.value && (
                    <Icon name="Check" size={14} color="white" />
                  )}
                </div>
                <span className="text-text-primary font-medium">{option.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-4">
        {currentQuestion > 0 && (
          <Button variant="outline" onClick={handleBack} iconName="ArrowLeft" iconPosition="left">
            Back
          </Button>
        )}
        <Button
          variant="default"
          fullWidth
          onClick={handleNext}
          disabled={!selectedOption}
          iconName="ArrowRight"
          iconPosition="right"
        >
          {currentQuestion < questions.length - 1 ? 'Next Question' : 'Get Recommendation'}
        </Button>
      </div>
    </div>
  );
};

export default PackageQuiz;